import { useRef, useState } from 'react'

import { ApiError } from '@/api/client'
import { mediaApi } from '@/api/endpoints'
import type { User } from '@/api/types'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { useToast } from '@/components/ui/Toast'
import { useT } from '@/i18n'
import { cn } from '@/lib/cn'

import { useUpdateProfile } from '../hooks/useProfile'

interface Props {
  user: User
  open: boolean
  onClose: () => void
}

export function EditProfileDialog({ user, open, onClose }: Props) {
  const t = useT()
  const update = useUpdateProfile()
  const toast = useToast()

  const [displayName, setDisplayName] = useState(user.display_name)
  const [bio, setBio] = useState(user.bio)
  const [location, setLocation] = useState(user.location)
  const [website, setWebsite] = useState(user.website)
  const [avatarUrl, setAvatarUrl] = useState(user.avatar_url)
  const [bannerUrl, setBannerUrl] = useState(user.banner_url)
  const [avatarMediaId, setAvatarMediaId] = useState<string | null>(null)
  const [bannerMediaId, setBannerMediaId] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)

  const avatarInput = useRef<HTMLInputElement>(null)
  const bannerInput = useRef<HTMLInputElement>(null)

  const error = update.error instanceof ApiError ? update.error : null

  const pickImage = async (file: File | undefined, kind: 'avatar' | 'banner') => {
    if (!file) return
    setUploading(true)
    try {
      const media = await mediaApi.upload(file)
      if (kind === 'avatar') {
        setAvatarMediaId(media.id)
        setAvatarUrl(media.thumb_url ?? media.url)
      } else {
        setBannerMediaId(media.id)
        setBannerUrl(media.medium_url ?? media.url)
      }
    } catch (uploadError) {
      toast.error(
        uploadError instanceof ApiError ? uploadError.message : t('composer.uploadFailed'),
      )
    } finally {
      setUploading(false)
    }
  }

  const save = () => {
    update.mutate(
      {
        display_name: displayName,
        bio,
        location,
        website,
        ...(avatarMediaId ? { avatar_media_id: avatarMediaId } : {}),
        ...(bannerMediaId ? { banner_media_id: bannerMediaId } : {}),
      },
      { onSuccess: onClose },
    )
  }

  return (
    <Dialog open={open} onClose={onClose} title={t('editProfile.title')}>
      <div className="pb-6">
        <div className="relative">
          <div className="h-36 bg-line-strong">
            {bannerUrl && <img src={bannerUrl} alt="" className="size-full object-cover" />}
          </div>
          <button
            type="button"
            onClick={() => bannerInput.current?.click()}
            className="absolute inset-0 m-auto size-11 rounded-md bg-black/60 text-xs font-bold text-white"
          >
            {t('editProfile.change')}
          </button>
          <input
            ref={bannerInput}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => void pickImage(e.target.files?.[0], 'banner')}
          />

          <div className="absolute -bottom-10 left-4">
            <div className="relative px-corners [--px-step:6px] border-4 border-surface bg-surface">
              <Avatar src={avatarUrl} alt={user.display_name} size="lg" />
              <button
                type="button"
                onClick={() => avatarInput.current?.click()}
                className="absolute inset-0 m-auto size-9 rounded-md bg-black/60 text-[11px] font-bold text-white"
              >
                {t('editProfile.change')}
              </button>
            </div>
            <input
              ref={avatarInput}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => void pickImage(e.target.files?.[0], 'avatar')}
            />
          </div>
        </div>

        <div className="mt-14 space-y-4 px-4">
          <LabeledInput
            label={t('editProfile.name')}
            value={displayName}
            maxLength={50}
            onChange={setDisplayName}
            error={error?.fieldError('display_name')}
          />
          <LabeledTextarea
            label={t('editProfile.bio')}
            value={bio}
            maxLength={160}
            onChange={setBio}
            error={error?.fieldError('bio')}
          />
          <LabeledInput
            label={t('editProfile.location')}
            value={location}
            maxLength={30}
            onChange={setLocation}
            error={error?.fieldError('location')}
          />
          <LabeledInput
            label={t('editProfile.website')}
            value={website}
            maxLength={100}
            onChange={setWebsite}
            error={error?.fieldError('website')}
            placeholder="example.com"
          />

          {error && !error.details && <p className="text-sm text-danger">{error.message}</p>}

          <Button
            fullWidth
            size="lg"
            onClick={save}
            loading={update.isPending}
            disabled={uploading}
          >
            {t('common.save')}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

function fieldClass(hasError: boolean) {
  return cn(
    'w-full rounded-md border bg-surface px-3 py-2 text-[15px] outline-none focus:border-brand-ink',
    hasError ? 'border-danger' : 'border-line-strong',
  )
}

function LabeledInput({
  label,
  value,
  onChange,
  error,
  ...rest
}: {
  label: string
  value: string
  onChange: (value: string) => void
  error?: string
  maxLength?: number
  placeholder?: string
}) {
  return (
    <div>
      <label className="mb-1 block text-sm text-ink-muted">{label}</label>
      <input
        {...rest}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={fieldClass(Boolean(error))}
      />
      {error && <p className="mt-1 text-sm text-danger">{error}</p>}
    </div>
  )
}

function LabeledTextarea({
  label,
  value,
  onChange,
  error,
  maxLength,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  error?: string
  maxLength?: number
}) {
  return (
    <div>
      <label className="mb-1 block text-sm text-ink-muted">{label}</label>
      <textarea
        value={value}
        maxLength={maxLength}
        rows={3}
        onChange={(event) => onChange(event.target.value)}
        className={cn(fieldClass(Boolean(error)), 'resize-none')}
      />
      {error && <p className="mt-1 text-sm text-danger">{error}</p>}
    </div>
  )
}
